import {
  CategoryName,
  DomainName,
  ExecutedActionP,
  ICheckGroupControl,
  TControlName,
  tg,
} from '@infotech-soe/metaschema';

import { SIGN_ACTION_TYPE } from '../../../../helpers/constants';
import { TActionArgsValues } from '../../../../infrastructure/providers/abstract/IApiProvider';
import CachedSchemaRepository from '../../../../infrastructure/schemas/storage/CachedSchemaRepository';
import IntlService from '../../../intl/IntlService';
import { isConditionForm, isPredefinedForm } from '../../typeguards';
import {
  CheckboxGroupFormControlViewModel,
  CheckboxGroupItemType,
  CheckboxGroupValueType,
  FormControlViewModelType,
  InputOption,
  LocalizedProps,
  SetCheckboxGroupFormControlViewModel,
} from '../../view-model/FormControlViewModel';
import { FormSource } from '../../view-model/FormSource';

import { getValueFromArgsOrConfig } from './helpers';

const extractDomainFromAction = async (
  item: ICheckGroupControl,
  category: CategoryName,
  source: FormSource
): Promise<DomainName | undefined> => {
  const action =
    !isConditionForm(source) && !isPredefinedForm(source)
      ? source.action
      : undefined;

  if (!action) {
    return;
  }
  const schemaRepository = new CachedSchemaRepository();
  const actionSchema = (await schemaRepository.getAction(
    category,
    action
  )) as ExecutedActionP;

  if (actionSchema.Type === SIGN_ACTION_TYPE) {
    return;
  }

  const arg = actionSchema.Args[item.name];

  if (arg) {
    if (tg.hasDomainProp(arg)) {
      return arg.domain;
    } else if (tg.hasCategoryFieldPath(arg)) {
      return (await schemaRepository.getField(category, arg.field)).domain;
    }
  }
};

const buildCheckGroupControl = async (
  item: ICheckGroupControl,
  localizedProps: LocalizedProps,
  args: TActionArgsValues,
  source: FormSource,
  formFieldDomains: Array<{ name: TControlName; domain: DomainName }>
): Promise<SetCheckboxGroupFormControlViewModel> => {
  const value = getValueFromArgsOrConfig<ICheckGroupControl['value'] | null>(
    item,
    args,
    source,
    null
  );
  const options: Array<InputOption<string | number>> = [];
  let itemType = CheckboxGroupItemType.NUMBER;
  let domain: string | undefined = undefined;
  const formFieldDomain = formFieldDomains.find(
    d => d.name === item.name
  )?.domain;
  if (formFieldDomain) {
    domain = formFieldDomain;
  } else if (isConditionForm(source)) {
    const cond = source.conditions[item.name];
    domain = typeof cond === 'string' ? cond : cond.domain;
  } else {
    domain = await extractDomainFromAction(item, source.category, source);
  }
  const schemaRepository = new CachedSchemaRepository();
  const domainSchema = domain
    ? await schemaRepository.getDomain(domain)
    : undefined;

  if (
    domainSchema !== undefined &&
    domain !== undefined &&
    domainSchema.type === 'enum' &&
    domainSchema.values
  ) {
    const intlService = new IntlService();
    for (const val of domainSchema.values) {
      if (tg.isString(val) || tg.isNumber(val)) {
        options.push({
          label: await intlService.getDomainEnumValue(domain, val),
          value: val,
          disabled: false,
        });
      } else {
        throw new Error(
          `No value set for domain ${JSON.stringify(
            domainSchema
          )}. Only "number | string" values allowed.`
        );
      }
    }
    if (typeof domainSchema.values[0] === 'string') {
      itemType = CheckboxGroupItemType.STRING;
    }
  }

  const getValue = (): CheckboxGroupFormControlViewModel['value'] => {
    if (!value) return null;

    const itemIsNumber = itemType === CheckboxGroupItemType.NUMBER;

    let properValue: Array<string | number> = [];

    if (typeof value === 'string')
      properValue = value.split(';').map(v => (itemIsNumber ? Number(v) : v));

    if (tg.isArray(value))
      properValue = value.map(v => (itemIsNumber ? Number(v) : String(v)));

    if (typeof value === 'number')
      properValue = [itemIsNumber ? value : String(value)];

    return properValue.filter(v => domainSchema?.values?.includes(v));
  };

  return {
    value: getValue(),
    columns: item.columns,
    name: item.name,
    type: FormControlViewModelType.CHECKBOX_GROUP,
    noLabel: item.noLabel,
    required: false,
    options,
    valueType: CheckboxGroupValueType.SET,
    hasValue: true,
    hasHistory: false,
    filterMode: true,
    visible: item.visible,
    disabled: item.disabled,
    items: item.items,
    storeValue: item.storeValue,
    ...localizedProps,
  };
};

export default buildCheckGroupControl;
